import { useState } from 'react'
import { CheckCircle, ShieldCheck } from '@phosphor-icons/react'
import { useI18n, type MessageKey } from '@/i18n'
import type { RosterRoll, TeamMember } from '@/offline'
import { Banner, Button, Field, ScanField } from '@/ui'
import { checkRollEnd, checkRollPair, codesInHand, type RollScanProblem } from '../model/scanGuard'
import type { ConfirmedRoll } from '../data/session'

const PROBLEM_KEYS: Record<RollScanProblem, MessageKey> = {
  UNKNOWN: 'gate.roll.unknown',
  WRONG_KIND: 'gate.roll.wrongKind',
  DIFFERENT_ROLLS: 'gate.roll.different',
  ORDER: 'gate.roll.order',
  NOT_ASSIGNED: 'gate.roll.notAssigned',
}

export type PendingRoll = Omit<ConfirmedRoll, 'command_id' | 'confirmed_at'>

/**
 * Scan the first and last tag of a roll (or seal of a strip) assigned to the operator (F4.1-j).
 * Refuses a code of the wrong kind, an unknown code, ends from different rolls; a roll not
 * assigned to her can be assigned on the spot by a supervisor's own PIN.
 */
export function RollConfirm({
  kind,
  operatorId,
  rolls,
  used,
  confirmed,
  onConfirm,
  onClear,
  requestSupervisor,
  spare = false,
}: {
  kind: 'BAG' | 'SEAL'
  operatorId: string
  rolls: readonly RosterRoll[]
  /** Codes bound or voided already (this phone + roster). */
  used: Iterable<string>
  confirmed: { roll: PendingRoll; actorId: string } | null
  onConfirm: (roll: PendingRoll, actorId: string | null) => void
  onClear: () => void
  requestSupervisor: (reason: string) => Promise<TeamMember | null>
  spare?: boolean
}) {
  const { t } = useI18n()
  const [first, setFirst] = useState<string | null>(null)
  const [last, setLast] = useState<string | null>(null)
  const label = kind === 'BAG' ? 'gate.roll.tagRoll' : 'gate.roll.sealStrip'
  const endError = (code: string) => {
    const r = checkRollEnd(code, kind, rolls)
    return r.ok ? null : t(PROBLEM_KEYS[r.problem])
  }

  if (confirmed) {
    return (
      <div className="flex items-center gap-3 rounded-card border border-ok/30 bg-ok-wash px-3 py-2.5">
        <CheckCircle size={24} weight="fill" className="shrink-0 text-ok" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[14px]">
            {confirmed.roll.first_payload} → {confirmed.roll.last_payload}
          </div>
          <div className="text-[13px] text-ink-soft">
            {t(kind === 'BAG' ? 'gate.roll.tagsInHand' : 'gate.roll.sealsInHand', {
              count: confirmed.roll.count,
            })}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClear}>
          {t('gate.change')}
        </Button>
      </div>
    )
  }

  const pair = first && last ? checkRollPair(first, last, kind, operatorId, rolls) : null
  const build = (override: boolean): PendingRoll | null => {
    if (!first || !last) return null
    const p = checkRollPair(first, last, kind, operatorId, rolls, override)
    if (!p.ok) return null
    return {
      batch_id: p.roll.batch_id,
      kind,
      first_payload: first,
      last_payload: last,
      first_seq: p.first,
      last_seq: p.last,
      count: codesInHand(p.roll, p.first, p.last, used),
      by: operatorId,
      spare: spare || (p.roll as RosterRoll).spare === true,
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <Field label={t(kind === 'BAG' ? 'gate.roll.firstTag' : 'gate.roll.firstSeal')} required>
        <ScanField
          value={first}
          onChange={(c) => setFirst(c)}
          validate={endError}
          sheetTitle={t(label)}
        />
      </Field>
      <Field label={t(kind === 'BAG' ? 'gate.roll.lastTag' : 'gate.roll.lastSeal')} required>
        <ScanField
          value={last}
          onChange={(c) => setLast(c)}
          validate={endError}
          sheetTitle={t(label)}
        />
      </Field>
      {pair && !pair.ok && (
        <Banner tone={pair.problem === 'NOT_ASSIGNED' ? 'warn' : 'error'}>
          {t(PROBLEM_KEYS[pair.problem])}
        </Banner>
      )}
      {pair?.ok && (
        <Button
          size="touch"
          fullWidth
          onClick={() => {
            const r = build(false)
            if (r) onConfirm(r, null)
          }}
        >
          {t(kind === 'BAG' ? 'gate.roll.confirmRoll' : 'gate.roll.confirmSeals')}
        </Button>
      )}
      {pair && !pair.ok && pair.problem === 'NOT_ASSIGNED' && (
        <Button
          variant="secondary"
          size="touch"
          fullWidth
          leadingIcon={<ShieldCheck aria-hidden="true" />}
          onClick={async () => {
            const sup = await requestSupervisor(t('gate.sup.assignRoll'))
            const r = sup ? build(true) : null
            if (sup && r) onConfirm({ ...r, by: sup.user_id }, sup.user_id)
          }}
        >
          {t('gate.sup.assignButton')}
        </Button>
      )}
    </div>
  )
}
